//Components
import DialogDelete from '../../components/DialogDelete/DialogDelete'
import Module from '../../../..//../components/layout/Module'
import DataGrid from '../../../../../components/table/DataGrid'
import TextAuditLog from '../../../../../components/text/TextAuditLog'
import IconTooltipButton from '../../../../../components/button/IconTooltipButton'
import MessageBubble from '../../components/MessageBubble/MessageBubble'
import FormatDate from '../../../../../components/date/FormatDate'
import StatusChip from '../../components/StatusChip/StatusChip'
import MenuButton from '../../../../button/MenuButton'
import SurveyStatisticsModal from '../SurveyStatistics/SurveyStatisticsModal'
import Filter from '../FilterList/Filter'
import ExportResponsesButton from './ExportResponsesButton'
//Other dependencies
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useMatch } from 'react-router-dom'
import { loadSurveys } from '../../../../../store/actions/dataEntrySurveyActions'
import { resetSurveyBuilder } from '../../../../../store/actions/dataEntrySurveyBuilderActions'
import { updateRowsPerPage, updateSkip } from '../../../../../store/actions/datatable.actions'
import { alertReset } from '../../../../../store/actions/alert.actions'
import {
  deleteSurvey,
  updateSurveyRecord,
  cloneSurvey
} from '../../../../../services/data-entry/surveys.service'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'

import { Chip } from '@mui/material'
import { STATUSDRAFT, STATUSACTIVE } from '../../constants/common'
import {
  SURVEY_TYPE_ABOUT_CLASSES,
  SURVEY_TYPE_ABOUT_TEACHER,
  SURVEY_TYPE_STANDARD
} from '../../constants/typeSurvey'
import './SurveysList.css'

const SurveysList = () => {
  const currentModule = 'dataentrysurveys'
  const match = useMatch('/surveys')
  const url = match ? match.pathname : ''
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [showDialog, setShowDialog] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showStatisticsModal, setShowStatisticsModal] = useState(false)
  const [selectedSurveyForStats, setSelectedSurveyForStats] = useState(null)
  const [isExporting, setIsExporting] = useState(false)
  const permissions = useSelector((state) => state?.session?.permissions?.dataentrysurveys ?? {})
  const loading = useSelector((state) => state?.dataEntrySurvey?.surveys?.loading || false)
  const totalRecords = useSelector((state) => state?.dataEntrySurvey?.surveys?.totalRecords || 0)
  const list = useSelector((state) => state?.dataEntrySurvey?.surveys?.list || [])
  const skip = useSelector((state) => state?.datatable?.surveyList?.skip || 0)
  const rowsPerPage = useSelector((state) => state?.datatable?.surveyList?.rowsPerPage || 100)
  const columnsTable = useSelector((state) => state?.datatable?.surveyList?.columnsTable || [])
  const state = useSelector((state) => state?.filter?.dataEntrySurvey || {})

  const loadData = useCallback(() => {
    const filters = {
      ...state?.filter
    }
    delete filters?.template
    delete filters?.roles
    dispatch(loadSurveys(skip * rowsPerPage, rowsPerPage, filters))
    dispatch(alertReset())
  }, [skip, rowsPerPage, state?.filter])

  useEffect(() => {
    loadData()
  }, [loadData])

  const cloneExistingSurvey = async (surveyId) => {
    try {
      const { error } = await cloneSurvey(surveyId)
      if (error) {
        enqueueSnackbar('We could not make a copy of this survey', {
          variant: 'error'
        })
      } else {
        enqueueSnackbar('Survey cloned successfully', {
          variant: 'success'
        })
        setSelectedRow(null)
        loadData()
      }
    } catch (error) {
      enqueueSnackbar('We could not make a copy of this survey', {
        variant: 'error'
      })
    }
  }

  const setCurrentRow = async (row, action) => {
    const data = JSON.parse(JSON.stringify(list.find((x) => x._id === row._id)))

    if (data) {
      if ('summary' === action) {
        dispatch(updateSkip('surveyPreviewList', 0))
        dispatch(updateRowsPerPage('surveyPreviewList', 50))

        setSelectedRow(data)
        navigate(`/dashboard/data-entry/surveys/${row._id}/preview`)
      }

      if ('statistics' === action) {
        setSelectedSurveyForStats(data)
        setShowStatisticsModal(true)
      }

      if ('delete' === action) {
        setSelectedRow(data)
        setShowDialog(true)
      }
      if ('edit' === action) {
        dispatch(updateSkip('surveyUsersList', 0))
        dispatch(updateRowsPerPage('surveyUsersList', 50))
        dispatch(resetSurveyBuilder())
        navigate(`/dashboard/data-entry/surveys/${row._id}/edit`)
      }

      if ('clone' === action) {
        cloneExistingSurvey(data._id)
      }
    }
  }

  const onCancelDialog = () => {
    setShowDialog(false)
    setSelectedRow(null)
  }

  const onConfirmDialog = async () => {
    try {
      setIsDeleting(true)
      const { error } = await deleteSurvey(selectedRow?._id)

      if (error) {
        enqueueSnackbar('We could not delete the survey', {
          variant: 'error'
        })
      } else {
        loadData()
      }
    } catch (error) {
      enqueueSnackbar('We could not delete the survey', {
        variant: 'error'
      })
    } finally {
      setIsDeleting(false)
      setShowDialog(false)
    }
  }

  const redirect = (redirectTo) => {
    navigate(`${url}/${redirectTo}`)
  }

  const onCreateSurvey = (type) => {
    dispatch(updateSkip('surveyUsersList', 0))
    dispatch(updateRowsPerPage('surveyUsersList', 50))
    dispatch(resetSurveyBuilder())

    redirect(`dashboard/data-entry/surveys/add/${type}`)
  }

  const getSurveyCompleted = (survey) => {
    const { percentageCompleted, totalUsers, totalCompleted } = survey || {}
    // Standard, "about classes" and "about teachers" surveys expose the expected
    // respondents (totalUsers), so show completion as "68.93% / (122/177)". Types
    // without a known roster (e.g. about users) fall back to the plain completed count.
    if (totalUsers > 0) {
      return `${
        !isNaN(percentageCompleted) ? percentageCompleted : '0.00'
      }% / (${totalCompleted}/${totalUsers})`
    }
    return `${survey?.totalCompleted || 0} user${survey?.totalCompleted === 1 ? '' : 's'}`
  }

  const onSaveNotes = async (payload = {}) => {
    try {
      const { name, value, recordId } = payload

      const { error } = await updateSurveyRecord(recordId, {
        _id: recordId,
        [name]: value
      })

      if (error) {
        enqueueSnackbar('We could not update the survey', {
          variant: 'error'
        })
      } else {
        enqueueSnackbar('Note updated successfully', {
          variant: 'success'
        })
      }
    } catch (error) {
      enqueueSnackbar('We could not update the survey', {
        variant: 'error'
      })
    }
  }

  const getStatusChip = (row = {}) => {
    const { status = STATUSACTIVE, hasExpired } = row
    let type = ''
    let content = ''

    if (hasExpired) {
      type = 'error'
      content = 'Closed'
    } else if (status === STATUSDRAFT) {
      type = 'active'
      content = 'Draft'
    } else {
      type = 'done'
      content = 'Active'
    }

    return <StatusChip content={content} type={type} />
  }

  const options = useMemo(() => {
    let options = []
    if (permissions?.create) {
      options.push({
        id: SURVEY_TYPE_STANDARD,
        label: 'Standard survey',
        permissions: true,
        onClick: () => onCreateSurvey(SURVEY_TYPE_STANDARD)
      })

      options.push({
        id: SURVEY_TYPE_ABOUT_TEACHER,
        label: 'Survey about users teachers',
        permissions: true,
        divider: true,
        onClick: () => onCreateSurvey(SURVEY_TYPE_ABOUT_TEACHER)
      })

      options.push({
        id: SURVEY_TYPE_ABOUT_CLASSES,
        label: 'Survey about classes',
        divider: true,
        permissions: true,
        onClick: () => onCreateSurvey(SURVEY_TYPE_ABOUT_CLASSES)
      })
    }

    return options
  }, [permissions])

  return (
    <Module>
      <Module.Header moduleName={currentModule} title='Surveys' titleLink={'/dashboard/data-entry/surveys'}>
        <div className='flex justify-end items-center gap-2'>
          {permissions?.create && (
            <MenuButton
              id='data-entry-surveys-create'
              icon='circle-check'
              title='Create Survey'
              color='secondary'
              items={options}
            />
          )}
        </div>
      </Module.Header>
      <Module.BodyHeader py='4' className='border-b border-b-2 border-primary'>
        <Filter />
      </Module.BodyHeader>
      <Module.BodyContainer className='overflow-y-auto'>
        <DataGrid
          className='pb-6'
          id='table-surveys'
          rows={list.map((row, index) => ({
            ...row,
            index: index + 1,
            name: row?.name || '-',
            template: row?.template?.title || '',
            completed: getSurveyCompleted(row),
            date: row?.dueDate ? <FormatDate date={row?.dueDate} /> : null,
            log: (
              <TextAuditLog
                updatedAt={row.updatedAt}
                updatedBy={row.updatedBy}
                createdBy={row.createdBy}
                createdAt={row.createdAt}
              />
            ),
            type: (
              <Chip
                label={row?.type.replace(/-/g, ' ')}
                size='small'
                className='uppercase'
                color={row?.type === SURVEY_TYPE_STANDARD ? 'primary' : 'secondary'}
              />
            ),
            note: (
              <MessageBubble
                value={row?.notes || ''}
                title={'Notes'}
                onSave={onSaveNotes}
                name='notes'
                recordId={row?._id}
              />
            ),
            status: <>{getStatusChip(row)}</>,
            actions: (
              <div className='flex items-center gap-1 items-center justify-center'>
                {permissions['show-one'] && (
                  <IconTooltipButton
                    id={`summary-${row._id}`}
                    disabled={loading || isExporting}
                    title='Summary'
                    icon={'fa-square-list'}
                    onClick={() => setCurrentRow(row, 'summary')}
                  />
                )}
                {permissions['show-one'] && (
                  <IconTooltipButton
                    id={`statistics-${row._id}`}
                    disabled={loading || isExporting}
                    title='Statistics'
                    icon={'fa-chart-line'}
                    onClick={() => setCurrentRow(row, 'statistics')}
                  />
                )}
                {permissions?.edit && (
                  <IconTooltipButton
                    id={`edit-${row._id}`}
                    disabled={loading || isExporting}
                    title='Edit'
                    icon='pencil'
                    onClick={() => setCurrentRow(row, 'edit')}
                  />
                )}
                {permissions?.create && (
                  <IconTooltipButton
                    id={`clone-${row._id}`}
                    disabled={loading || isExporting}
                    title='Make a copy'
                    icon='clone'
                    onClick={() => setCurrentRow(row, 'clone')}
                  />
                )}
                {permissions['show-one'] && (
                  <ExportResponsesButton
                    survey={row}
                    surveyId={row?._id}
                    isExporting={isExporting}
                    setIsExporting={setIsExporting}
                  />
                )}
                {permissions?.delete && (
                  <IconTooltipButton
                    id={`delete-${row._id}`}
                    disabled={loading || isExporting}
                    title='Delete'
                    icon='fa-trash'
                    onClick={() => setCurrentRow(row, 'delete')}
                  />
                )}
              </div>
            )
          }))}
          loading={loading}
          labelNoRows='No surveys found'
          nameModule='surveyList'
          headers={[...columnsTable]}
          totalRows={totalRecords}
          skip={skip}
          rowsPerPage={rowsPerPage}
          columns={[
            {
              label: '#',
              key: 'index',
              align: 'center',
              width: 10,
              show: true,
              hide: false
            },
            ...columnsTable,
            {
              label: 'Actions',
              key: 'actions',
              align: 'center',
              show: true,
              hide: false
            }
          ]}
          orderByField='name'
        />
      </Module.BodyContainer>
      <DialogDelete
        show={showDialog}
        onCancel={onCancelDialog}
        onConfirm={onConfirmDialog}
        loading={isDeleting}
        type='delete'
        item={selectedRow?.name}
        module='survey'
      />

      <SurveyStatisticsModal
        open={showStatisticsModal}
        onClose={() => {
          setShowStatisticsModal(false)
          setSelectedSurveyForStats(null)
        }}
        surveyId={selectedSurveyForStats?._id}
        surveyName={selectedSurveyForStats?.name}
        type={selectedSurveyForStats?.type}
      />
    </Module>
  )
}
export default SurveysList
